import 'twin.macro'
import { useSelect } from 'downshift'
import type { ReactElement, Ref } from 'react'
import { forwardRef } from 'react'

import {
  DropdownWrapper,
  DropdownButton,
  DropdownSelected,
  DropdownList,
  DropdownListItem,
  DropdownTitle,
  DropdownSubtitle,
  DropdownArrow,
} from '../DropdownAtoms/DropdownAtoms'
import type { SelectProps } from './types'
import itemToItemTitle from '../../internal/itemToItemTitle'

function Select<ItemValue>(
  {
    selectedItem,
    initialSelectedItem,
    items,
    placeholder = '',
    onSelectedItemChange,
    onBlur,
    name,
    id,
    error,
    disabled,
    ...formFieldForwardedProps
  }: SelectProps<ItemValue>,
  ref: Ref<HTMLButtonElement>,
) {
  const {
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen,
    selectedItem: localSelectedItem,
  } = useSelect({
    id,
    items,
    onSelectedItemChange,
    itemToString: itemToItemTitle,
    // Comparing with undefined is necessary because
    // selectedItem as null is a valid value and means something
    // https://github.com/downshift-js/downshift#control-props
    ...(selectedItem !== undefined
      ? { selectedItem }
      : { initialSelectedItem }),
  })

  return (
    <DropdownWrapper>
      {/*
        {...rest} overrides {...getToggleButtonProps()}
        to make Select compatible with FormField
      */}
      <DropdownButton
        type="button"
        $isPlaceholder={localSelectedItem == null}
        $hasError={error}
        {...getToggleButtonProps({
          name,
          ref,
          onBlur,
          disabled,
          ...formFieldForwardedProps,
        })}
      >
        <DropdownSelected>
          {localSelectedItem ? localSelectedItem.title : placeholder}
        </DropdownSelected>
        <DropdownArrow />
      </DropdownButton>
      {/*
        Menu renders to DOM even if closed for this is the a11y way (see issue below)
        So we show/hide it with good old display none
        https://github.com/downshift-js/downshift/pull/1053#issuecomment-642113835
      */}
      <DropdownList
        {...getMenuProps({
          onKeyDown(e) {
            // prevent the esc keypress from bubbling up the dom
            // useful to close the menu instead of closing a whole dialog
            if (e.key === 'Escape') e.stopPropagation()
          },
        })}
        $isVisible={isOpen}
        tw="mt-1.5 absolute"
      >
        {items.map((item, index) => {
          const isSelected = localSelectedItem?.value === item.value

          return (
            <DropdownListItem
              key={`${item.value}${index}`}
              startSlot={item.startSlot}
              endSlot={item.endSlot}
              hasSubtitle={Boolean(item.subtitle)}
              isHighlighted={highlightedIndex === index}
              {...getItemProps({
                item,
                index,
                isSelected,
                disabled: item.disabled,
                'aria-disabled': item.disabled,
              })}
            >
              {item.title && (
                <DropdownTitle $isSelected={isSelected}>
                  {item.title}
                </DropdownTitle>
              )}
              {item.subtitle && (
                <DropdownSubtitle $isSelected={isSelected}>
                  {item.subtitle}
                </DropdownSubtitle>
              )}
            </DropdownListItem>
          )
        })}
      </DropdownList>
    </DropdownWrapper>
  )
}

// This ugly thing is needed because we need to pass a generic (ItemValue) to the component
// but forwardRef doesnt support that
export default forwardRef(Select) as <Value>(
  props: SelectProps<Value> & { ref?: Ref<HTMLButtonElement> },
) => ReactElement
